'use client';

import type { Key } from 'react';
import { useMemo, useRef, useState } from 'react';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { Search, X } from 'lucide-react';

type Option<T extends Key> = {
  readonly label: string;
  readonly value: T;
};

type SimpleChecklistFilterProps<T extends Key> = {
  readonly options: readonly Option<T>[];
  readonly selectedValues: readonly T[];
  readonly onChangeSelection: (selectedValues: T[]) => void;
  readonly enableSearch?: boolean;
  readonly onClearAll?: () => void;
};

/**
 * Generic checklist filter component for simple select scenarios
 * Used for filters like Stage, Lead Source, Status, Service Calendar, etc.
 */
export function SimpleChecklistFilter<T extends Key>({
  options,
  selectedValues,
  onChangeSelection,
  enableSearch = false,
  onClearAll,
}: SimpleChecklistFilterProps<T>) {
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!enableSearch || search === '') return options;
    return options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search, enableSearch]);

  const selectedOptions = useMemo(() => {
    return filteredOptions.filter((option) => selectedValues.includes(option.value));
  }, [filteredOptions, selectedValues]);

  const unselectedOptions = useMemo(() => {
    return filteredOptions.filter((option) => !selectedValues.includes(option.value));
  }, [filteredOptions, selectedValues]);

  const handleToggle = (option: Option<T>) => {
    const isChecked = selectedValues.includes(option.value);
    if (isChecked) {
      onChangeSelection(selectedValues.filter((v) => v !== option.value));
    } else {
      onChangeSelection([...selectedValues, option.value]);
    }
  };

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      onChangeSelection([]);
    }
  };

  const itemClassName =
    'w-full px-3 py-2 cursor-pointer hover:bg-component-hover flex items-center gap-2 min-w-0 text-left focus:outline-none focus-visible:bg-component-hover';
  const labelClassName = 'px-3 py-1.5 text-xs font-medium text-secondary';
  const separatorClassName = 'bg-border-light h-px mt-1';

  const handleKeyDown = (e: React.KeyboardEvent, option: Option<T>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(option);
    }
  };

  return (
    <>
      {enableSearch && (
        <>
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-secondary" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (searchInputRef.current) {
                    setTimeout(() => {
                      searchInputRef.current?.focus();
                    }, 0);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.stopPropagation();
                  }
                }}
                className="pl-8 h-10 w-full min-w-0 rounded-md border border-light bg-transparent px-3 py-1 text-dark shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-border-light focus-visible:ring-2"
              />
            </div>
          </div>
          <div className={separatorClassName} />
        </>
      )}
      <div className="max-h-[40vh] overflow-y-auto overflow-x-hidden">
        {selectedOptions.length > 0 && (
          <>
            <div className={labelClassName}>Selected</div>
            {selectedOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleToggle(option)}
                onKeyDown={(e) => handleKeyDown(e, option)}
                role="checkbox"
                aria-checked={true}
                tabIndex={0}
                className={itemClassName}
              >
                <Checkbox checked={true} className="shrink-0 pointer-events-none" tabIndex={-1} />
                <span className="truncate min-w-0">{option.label}</span>
              </div>
            ))}
            {unselectedOptions.length > 0 && <div className={separatorClassName} />}
          </>
        )}
        {unselectedOptions.length > 0 && (
          <>
            {unselectedOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleToggle(option)}
                onKeyDown={(e) => handleKeyDown(e, option)}
                role="checkbox"
                aria-checked={false}
                tabIndex={0}
                className={itemClassName}
              >
                <Checkbox checked={false} className="shrink-0 pointer-events-none" tabIndex={-1} />
                <span className="truncate min-w-0">{option.label}</span>
              </div>
            ))}
          </>
        )}
        {selectedOptions.length === 0 && unselectedOptions.length === 0 && (
          <div className="px-2 py-4 text-center text-sm text-secondary">No options</div>
        )}
      </div>
      {selectedValues.length > 0 && (
        <>
          <div className={separatorClassName} />
          <button
            type="button"
            onClick={handleClearAll}
            className="w-full px-3 py-2 cursor-pointer hover:bg-component-hover text-primary flex items-center gap-2 bg-transparent border-0 text-left"
          >
            <X className="size-4 text-primary" />
            Clear all
          </button>
        </>
      )}
    </>
  );
}
